import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Save,
  Megaphone,
  SearchCheck,
  FolderTree,
} from "lucide-react";
import api from "../../../api/api";

const emptySEO = {
  meta_title: "",
  meta_description: "",
  focus_keyword: "",
  keywords: [],
  canonical_url: "",
  og_title: "",
  og_description: "",
  og_image: "",
  no_index: false,
  no_follow: false,
};

export default function AdminAnnouncementForm() {
  const { id } = useParams();
  const navigate = useNavigate();

  const isEdit = Boolean(id);

  const [categories, setCategories] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEdit);

  const [keywordInput, setKeywordInput] = useState("");

  const [form, setForm] = useState({
    title: "",
    summary: "",
    content: "",
    status: "draft",
    category_id: "",
    button_text: "Read Full Announcement",
    published_at: "",
    seo: emptySEO,
  });

  const selectedCategory = useMemo(() => {
    return categories.find((cat) => cat.id === form.category_id);
  }, [categories, form.category_id]);

  const fetchCategories = async () => {
    try {
      const res = await api.get("/announcement-categories");
      setCategories(res.data || []);
    } catch (error) {
      console.error("Failed to fetch announcement categories:", error);
    }
  };

  const fetchAnnouncement = async () => {
    if (!id) return;

    setLoading(true);

    try {
      const res = await api.get("/announcements", {
        params: {
          limit: 100,
        },
      });

      const item = (res.data.items || []).find((announcement) => {
        return announcement.id === id;
      });

      if (!item) {
        alert("Announcement not found.");
        navigate("/admin/announcements");
        return;
      }

      setForm({
        title: item.title || "",
        summary: item.summary || "",
        content: item.content || "",
        status: item.status || "draft",
        category_id: item.category_id || "",
        button_text: item.button_text || "Read Full Announcement",
        published_at: item.published_at
          ? item.published_at.slice(0, 16)
          : "",
        seo: {
          ...emptySEO,
          ...(item.seo || {}),
        },
      });

      setKeywordInput((item.seo?.keywords || []).join(", "));
    } catch (error) {
      console.error("Failed to fetch announcement:", error);
      alert("Failed to load announcement.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
    fetchAnnouncement();
  }, [id]);

  const updateField = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const updateSEO = (field, value) => {
    setForm((prev) => ({
      ...prev,
      seo: {
        ...prev.seo,
        [field]: value,
      },
    }));
  };

  const buildPayload = () => {
    const payload = {
      title: form.title.trim(),
      summary: form.summary.trim(),
      content: form.content,
      status: form.status,
      category_id: form.category_id || null,
      button_text: form.button_text.trim() || "Read Full Announcement",
      seo: {
        ...form.seo,
        keywords: keywordInput
          .split(",")
          .map((item) => item.trim())
          .filter(Boolean),
      },
    };

    if (form.published_at) {
      payload.published_at = new Date(form.published_at).toISOString();
    }

    return payload;
  };

  const submitForm = async (e) => {
    e.preventDefault();

    if (!form.title.trim()) {
      alert("Announcement title is required.");
      return;
    }

    if (!form.content.trim()) {
      alert("Announcement content is required.");
      return;
    }

    setSaving(true);

    try {
      const payload = buildPayload();

      if (isEdit) {
        await api.put(`/announcements/${id}`, payload);
      } else {
        await api.post("/announcements", payload);
      }

      navigate("/admin/announcements");
    } catch (error) {
      console.error("Failed to save announcement:", error);
      alert(
        error?.response?.data?.detail ||
          "Failed to save announcement. Please try again."
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-3xl border border-slate-200 p-10 text-center font-bold text-slate-500">
        Loading announcement...
      </div>
    );
  }

  return (
    <form onSubmit={submitForm} className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <Link
            to="/admin/announcements"
            className="inline-flex items-center gap-2 text-sm font-bold text-slate-500 hover:text-[#061A40]"
          >
            <ArrowLeft size={17} />
            Back to announcements
          </Link>

          <h1 className="text-2xl md:text-3xl font-black text-[#061A40] mt-3">
            {isEdit ? "Edit Announcement" : "Create Announcement"}
          </h1>

          <p className="text-sm text-slate-500 mt-1">
            Create official public notices that can appear on the homepage announcement strip.
          </p>
        </div>

        <button
          type="submit"
          disabled={saving}
          className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl bg-[#061A40] text-white font-black hover:bg-[#0A2A62] disabled:opacity-60"
        >
          <Save size={18} />
          {saving ? "Saving..." : "Save Announcement"}
        </button>
      </div>

      <div className="grid xl:grid-cols-[1fr_360px] gap-6">
        <div className="space-y-6">
          <section className="bg-white rounded-3xl border border-slate-200 shadow-sm p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-11 h-11 rounded-2xl bg-[#F4B000]/15 text-[#061A40] flex items-center justify-center">
                <Megaphone size={22} />
              </div>

              <div>
                <h2 className="font-black text-[#061A40]">
                  Announcement Content
                </h2>
                <p className="text-xs text-slate-500">
                  Title, summary and full announcement details.
                </p>
              </div>
            </div>

            <div className="space-y-5">
              <div>
                <label className="block text-sm font-bold text-slate-700 mb-2">
                  Announcement Title
                </label>

                <input
                  value={form.title}
                  onChange={(e) => updateField("title", e.target.value)}
                  placeholder="Enter announcement title"
                  className="w-full px-4 py-3 rounded-2xl border border-slate-200 outline-none focus:border-[#061A40] font-semibold"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-slate-700 mb-2">
                  Short Summary
                </label>

                <textarea
                  value={form.summary}
                  onChange={(e) => updateField("summary", e.target.value)}
                  placeholder="Short announcement summary shown on listing page"
                  rows={4}
                  className="w-full px-4 py-3 rounded-2xl border border-slate-200 outline-none focus:border-[#061A40]"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-slate-700 mb-2">
                  Full Announcement Content
                </label>

                <textarea
                  value={form.content}
                  onChange={(e) => updateField("content", e.target.value)}
                  placeholder="You can enter HTML content here."
                  rows={16}
                  className="w-full px-4 py-3 rounded-2xl border border-slate-200 outline-none focus:border-[#061A40] font-mono text-sm"
                />
              </div>
            </div>
          </section>

          <section className="bg-white rounded-3xl border border-slate-200 shadow-sm p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-11 h-11 rounded-2xl bg-[#061A40]/10 text-[#061A40] flex items-center justify-center">
                <SearchCheck size={22} />
              </div>

              <div>
                <h2 className="font-black text-[#061A40]">
                  SEO Settings
                </h2>
                <p className="text-xs text-slate-500">
                  Search engine metadata for this announcement.
                </p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <label className="block text-sm font-bold text-slate-700 mb-2">
                  Meta Title
                </label>

                <input
                  value={form.seo.meta_title}
                  onChange={(e) => updateSEO("meta_title", e.target.value)}
                  maxLength={60}
                  className="w-full px-4 py-3 rounded-2xl border border-slate-200 outline-none focus:border-[#061A40]"
                />

                <p className="text-xs text-slate-400 mt-1">
                  {form.seo.meta_title.length}/60
                </p>
              </div>

              <div>
                <label className="block text-sm font-bold text-slate-700 mb-2">
                  Focus Keyword
                </label>

                <input
                  value={form.seo.focus_keyword}
                  onChange={(e) => updateSEO("focus_keyword", e.target.value)}
                  className="w-full px-4 py-3 rounded-2xl border border-slate-200 outline-none focus:border-[#061A40]"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-bold text-slate-700 mb-2">
                  Meta Description
                </label>

                <textarea
                  value={form.seo.meta_description}
                  onChange={(e) =>
                    updateSEO("meta_description", e.target.value)
                  }
                  maxLength={160}
                  rows={3}
                  className="w-full px-4 py-3 rounded-2xl border border-slate-200 outline-none focus:border-[#061A40]"
                />

                <p className="text-xs text-slate-400 mt-1">
                  {form.seo.meta_description.length}/160
                </p>
              </div>

              <div>
                <label className="block text-sm font-bold text-slate-700 mb-2">
                  Keywords
                </label>

                <input
                  value={keywordInput}
                  onChange={(e) => setKeywordInput(e.target.value)}
                  placeholder="oil, gas, free zone"
                  className="w-full px-4 py-3 rounded-2xl border border-slate-200 outline-none focus:border-[#061A40]"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-slate-700 mb-2">
                  Canonical URL
                </label>

                <input
                  value={form.seo.canonical_url}
                  onChange={(e) => updateSEO("canonical_url", e.target.value)}
                  className="w-full px-4 py-3 rounded-2xl border border-slate-200 outline-none focus:border-[#061A40]"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-slate-700 mb-2">
                  OG Title
                </label>

                <input
                  value={form.seo.og_title}
                  onChange={(e) => updateSEO("og_title", e.target.value)}
                  className="w-full px-4 py-3 rounded-2xl border border-slate-200 outline-none focus:border-[#061A40]"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-slate-700 mb-2">
                  OG Image URL
                </label>

                <input
                  value={form.seo.og_image}
                  onChange={(e) => updateSEO("og_image", e.target.value)}
                  className="w-full px-4 py-3 rounded-2xl border border-slate-200 outline-none focus:border-[#061A40]"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-bold text-slate-700 mb-2">
                  OG Description
                </label>

                <textarea
                  value={form.seo.og_description}
                  onChange={(e) => updateSEO("og_description", e.target.value)}
                  rows={3}
                  className="w-full px-4 py-3 rounded-2xl border border-slate-200 outline-none focus:border-[#061A40]"
                />
              </div>

              <label className="flex items-center gap-3 font-bold text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={form.seo.no_index}
                  onChange={(e) => updateSEO("no_index", e.target.checked)}
                />
                No Index
              </label>

              <label className="flex items-center gap-3 font-bold text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={form.seo.no_follow}
                  onChange={(e) => updateSEO("no_follow", e.target.checked)}
                />
                No Follow
              </label>
            </div>
          </section>
        </div>

        <aside className="space-y-6">
          <section className="bg-white rounded-3xl border border-slate-200 shadow-sm p-6">
            <h2 className="font-black text-[#061A40] mb-5">
              Publish Settings
            </h2>

            <div className="space-y-5">
              <div>
                <label className="block text-sm font-bold text-slate-700 mb-2">
                  Status
                </label>

                <select
                  value={form.status}
                  onChange={(e) => updateField("status", e.target.value)}
                  className="w-full px-4 py-3 rounded-2xl border border-slate-200 outline-none focus:border-[#061A40]"
                >
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                  <option value="scheduled">Scheduled</option>
                  <option value="archived">Archived</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-bold text-slate-700 mb-2">
                  Publish Date
                </label>

                <input
                  type="datetime-local"
                  value={form.published_at}
                  onChange={(e) => updateField("published_at", e.target.value)}
                  className="w-full px-4 py-3 rounded-2xl border border-slate-200 outline-none focus:border-[#061A40]"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-slate-700 mb-2">
                  Homepage Button Text
                </label>

                <input
                  value={form.button_text}
                  onChange={(e) => updateField("button_text", e.target.value)}
                  className="w-full px-4 py-3 rounded-2xl border border-slate-200 outline-none focus:border-[#061A40]"
                />
              </div>
            </div>
          </section>

          <section className="bg-white rounded-3xl border border-slate-200 shadow-sm p-6">
            <div className="flex items-center gap-3 mb-5">
              <FolderTree size={20} className="text-[#061A40]" />
              <h2 className="font-black text-[#061A40]">
                Category
              </h2>
            </div>

            <select
              value={form.category_id}
              onChange={(e) => updateField("category_id", e.target.value)}
              className="w-full px-4 py-3 rounded-2xl border border-slate-200 outline-none focus:border-[#061A40]"
            >
              <option value="">Uncategorized</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </select>

            {selectedCategory && (
              <p className="mt-3 text-sm text-slate-500">
                {selectedCategory.description}
              </p>
            )}

            <Link
              to="/admin/announcement-categories"
              className="mt-5 inline-flex text-sm font-black text-[#061A40] hover:text-[#F4B000]"
            >
              Manage announcement categories
            </Link>
          </section>
        </aside>
      </div>
    </form>
  );
}